<template>
  <div class="clearfix">
    <div
      id="previewSubmissionModal"
      ref="previewSubmissionModal"
      class="reveal preview-submission-modal"
      data-reveal
    >
      <button
        class="close-button"
        data-close
        aria-label="Close modal"
        type="button"
      >
        <span aria-hidden="true">&times;</span>
      </button>
      <div class="tender-confirm clearfix">
        <div class="submission-email-subject">
          <label>{{ $t('eCircular.subject') }}</label>
          <div v-if="currentTenantSubmission">
            {{
              $t(`tenantProjects.tenantViewComments.batchNumberComments`, [Number(currentTenantSubmission.order) + 1]) +
                ` - ${currentTenantProject.projectName}`
            }}
          </div>
        </div>
        <EmailEditFields v-if="canSend" />
        <div class="comments-preview-body-warpper">
          <CommentsPreviewBody
            :isBatchesView="false"
            :activeBatch="currentTenantSubmission"
          />
        </div>
        <TenantSubmissionDocuments
          v-if="modalInitiated"
          :files.sync="files"
          :sourceId="(currentTenantSubmission || {}).id || ''"
          :canEdit="false"
        />
        <CommentHistory :submission="currentTenantSubmission" />
        <div
          v-if="canSend"
          class="row align-right nav_btn"
        >
          <button
            id="confirm_no"
            v-hkldreset
            type="button"
            class="btn btn--darkblue"
            :disabled="!hasPendingSubmission"
            @click="saveReceivers()"
          >{{ $t('action.save') }}</button>
          <button
            id="confirm_ok"
            v-hkldreset
            type="button"
            class="btn btn--orange"
            :disabled="disableSend || hasChangedToFU"
            @click="openSubmitConfirm()"
          >{{ $t('action.send') }}</button>
        </div>
        <div
          v-if="(!isDesignCommentFulfilled || !hasPendingSubmission)"
          class="error-msg"
        >
          {{ completeErrMsg }}
        </div>
        <div
          v-if="!isMandatoryFieldsValid && isReinstatement"
          class="error-msg"
        >
          {{ $t('tenantProjects.mandatoryFieldsMissed') }}
        </div>
      </div>
    </div>

    <ActionConfirmModal
      ref="submitConfirmModal"
      :modalId="submissionSendConfId"
      :confirmTitle="confirmTitle"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { computed, getCurrentInstance } from 'vue-demi'
import ActionConfirmModal from '@/controls/ActionConfirmModal'
import * as regex from '@/utils/regex'
import EventBus from '@/utils/eventBus'
import i18n from '@/utils/i18n'
import mutationTypes from '@/store/mutation-types'
import notification from '@/utils/notification'
import numberUtils from '@/utils/numberUtils'
import { DesignBatchStatus, tenantProjectStatus, tenantProjectTypes } from '@/utils/locale'
import { EmailTypes } from '@/utils/consts'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { getTenantSubmission, updateReinstatementAndSendSubmissionEmail } from '@/api/tenantProjects/request'
import { processDuplicateEmails } from '@/api/postman/util'
import { sendEmail } from '@/api/postman/request'
import { utils as formUtils } from '@/plugin/InputChange'
import { useStoreGetters } from '@/store/index'
import tenantProjectsNotification from '@/api/postman/tenantProject'
import { useRouteParamId } from '@/router/useRouter'
import EmailEditFields from '../EmailEditFields'
import { canSendInPreviewModel, useEmailTool } from '../hook'
import previewSubmissionModalMixin from './previewSubmissionModalMixin'
import TenantSubmissionDocuments from './TenantSubmissionDocuments'
import CommentsPreviewBody from './CommentsPreviewBody'
import CommentHistory from './CommentHistory'

export default {
  name: 'PreviewSubmissionModal',
  components: {
    ActionConfirmModal,
    CommentHistory,
    CommentsPreviewBody,
    TenantSubmissionDocuments,
    EmailEditFields,
  },
  mixins: [previewSubmissionModalMixin],
  setup () {
    const { proxy } = getCurrentInstance()
    const tpId = useRouteParamId()
    const canSend = canSendInPreviewModel()
    const { to, cc, bcc, init: emailInit } = useEmailTool()
    const { currentTenantProject, currentTenantSubmission } = useStoreGetters('tenantProject', ['currentTenantProject', 'currentTenantSubmission'])
    const isFitout = computed(() => {
      return currentTenantProject.value.type === tenantProjectTypes.Fitout
    })
    const isReinstatement = computed(() => {
      return currentTenantProject.value.type === tenantProjectTypes.Reinstatement
    })
    const isMandatoryFieldsValid = computed(() => {
      const fields = ['projectLocation', 'floors', 'type', 'description', 'tentativeDate', 'estimatedDuration']
      return Object.entries(currentTenantProject.value)
        .filter(([key]) => fields.includes(key))
        .every(([_, value]) => {
          return lodash.toString(value)
        })
    })
    const disableSend = computed(() => {
      proxy.validateDesignComments(proxy.designComment)
      const isCpFollowUp = proxy.designComment.comment === DesignBatchStatus.PROCEED_CP_FOLLOW_UP
      const noDesignComment = !(proxy.designComment && proxy.designComment.comment)
      const isFitOutWithoutDesignComment = isFitout.value && noDesignComment
      const hasReviewerOrDocsOfDesign = proxy.currentDesignReviewer || proxy.isExistingDesignDocs
      const isInValidReinstatement = isReinstatement.value && !isMandatoryFieldsValid.value
      return isInValidReinstatement || isCpFollowUp || !proxy.hasPendingSubmission ||
      (isFitOutWithoutDesignComment && hasReviewerOrDocsOfDesign)
    })
    const isDraft = computed(() => {
      return currentTenantProject.value.status === tenantProjectStatus.DRAFT
    })
    const sendAndSubmittedReinstatement = async (mailMetadata) => {
      const metadata = tenantProjectsNotification.completeSubmission(mailMetadata)
      await updateReinstatementAndSendSubmissionEmail(tpId.value, {
        email: lodash.get(metadata, '0.mailRequest'),
        submission: currentTenantSubmission.value,
      })
    }
    const confirmTitle = computed(() => {
      if (isReinstatement.value && isDraft.value) {
        return [i18n.t('tenantProjects.letterSentAndSubmitted'), i18n.t('confirmSendLetter')].join('<br>')
      }
      return i18n.t('confirmSendLetter')
    })
    return {
      confirmTitle,
      tpId,
      sendAndSubmittedReinstatement,
      isDraft,
      canSend,
      to,
      cc,
      bcc,
      emailInit,
      disableSend,
      isFitout,
      isReinstatement,
      isMandatoryFieldsValid,
    }
  },
  data () {
    return {
      submissionSendConfId: `submissionSendConfId-${new Date().getTime()}-${Math.floor(Math.random() * 1000)}`,
      regex,
      files: [],
      modalInitiated: false,
      isDesignCommentFulfilled: true,
      completeErrMsg: '',
      proceedConfirmMsg: '',
      hasChangedToFU: false,
    }
  },

  computed: {
    ...mapGetters('tenantProject',
      ['currentTenantProject', 'accounts', 'currentTenantSubmission', 'activeSubmissionIndex', 'currentDesignReviewer', 'currentTechReviewer', 'hasPendingSubmission', 'currentTenantProjectDocs']),
    ...mapGetters('user', ['currentUser', 'currentUserEmail']),
    techComment () {
      return (this.currentTenantSubmission && this.currentTenantSubmission.techComment) || {}
    },
    designComment () {
      return (this.currentTenantSubmission && this.currentTenantSubmission.designComment) || {}
    },
    comments () {
      return (this.techComment && this.techComment.comments) || []
    },
    emailReceivers () {
      return this.techComment && this.techComment.emailReceivers
    },
    tenancy () {
      return this.currentTenantProject && this.currentTenantProject.tenancy
    },
    tenancyUserEmails () {
      const entitledAccounts = this.tenancy.accounts.filter(account => account.TenancyAccount && account.TenancyAccount.invitedAt && account.TenancyAccount.perms.TenantProject)
      return entitledAccounts.map(account => account.username)
    },
    historyCommenters () {
      return (this.currentTenantSubmission.actionHistory || []).map(history => history.user) || []
    },
    isDesignPic () {
      return this.currentDesignReviewer && this.currentDesignReviewer.id === this.currentUser.id
    },
    isExistingDesignDocs () {
      const currentSubmissionDesignDocs = this.currentTenantProjectDocs.filter(doc => doc.batchNum !== null && Number(doc.batchNum) === Number(lodash.get(this.currentTenantSubmission, 'order')) && doc.metaData && doc.metaData.submitted && doc.metaData.isDesign) || []
      return currentSubmissionDesignDocs.length
    },
  },
  beforeDestroy () {
    if (this.previewSubmissionModal) {
      this.previewSubmissionModal.destroy()
      delete this.previewSubmissionModal
    }
    $(this.$refs.previewSubmissionModal).remove()
  },
  methods: {
    ...mapActions('tenantProject', [
      'loadTenantProject',
      'updateCurrentTenantSubmission',
      'logSubmissionAction',
      'updateCurSubmissionExpDesign',
    ]),
    ...mapMutations('tenantProject', {
      updateFieldByPath: mutationTypes.TENANT_PROJECT_UPDATE_FIELD_PATH,
      setStartReview: mutationTypes.TENANT_PROJECT_SET_START_REVIEW,
    }),
    async init () {
      if (!this.previewSubmissionModal) {
        this.previewSubmissionModal = await new Foundation.Reveal($('#previewSubmissionModal'), {
          closeOnEsc: false,
          closeOnClick: false,
        })
        this.previewSubmissionModal.$element.on('closed.zf.reveal', () => {
          fusionInputCheckUtil.resetAllFields()
        })
      }
      setTimeout(() => { this.modalInitiated = true }, 0)
    },
    async openModal () {
      this.modalInitiated = false
      await this.init()
      const submission = await getTenantSubmission(this.currentTenantSubmission.id)
      if (submission.completedDate) {
        await this.loadTenantProject(this.$router.currentRoute.params.id)
        notification.fireNotification(false, this.$i18n.t('tenantProjects.alreadyCompleted'))
        return
      }
      if (this.canSend) {
        await this.emailInit()
      }

      if (!this.isDesignPic) {
        await this.updateCurSubmissionExpDesign({needNotificate: false})
      } else {
        await this.updateCurrentTenantSubmission({needNotificate: false})
      }

      await this.loadTenantProject(this.$router.currentRoute.params.id)
      this.previewSubmissionModal.open()
      setTimeout(formUtils.resizeElements, 0)
      fusionInputCheckUtil.validateAllFields(undefined, '#previewSubmissionModal')
    },
    closeModal () {
      if (this.previewSubmissionModal) {
        this.previewSubmissionModal.close()
      }
      this.completeErrMsg = ''
    },
    getAlphabet (val) {
      return `${numberUtils.convertToAlphabet(parseInt(val + 1))}.`
    },
    getPrefix (index) {
      return `${index + 1}.`
    },
    getPath () {
      return ['submissions', this.activeSubmissionIndex, 'techComment', 'emailReceivers']
    },
    setReceiversToStore () {
      this.updateFieldByPath({
        path: this.getPath(),
        newValue: this.getUniqueEmails(),
      })
    },
    async saveReceivers (notify = true) {
      if (!this.validateReceivers()) {
        return
      }
      this.setReceiversToStore()
      if (!this.isDesignPic) {
        await this.updateCurSubmissionExpDesign({needNotificate: notify})
      } else {
        await this.updateCurrentTenantSubmission({needNotificate: notify})
      }
      await this.loadTenantProject(this.$router.currentRoute.params.id)
    },
    validateDesignComments (designComment) {
      this.isDesignCommentFulfilled = true
      this.hasChangedToFU = false
      if (!this.isFitout) {
        return
      }
      if (!(designComment && designComment.comment) && (this.isExistingDesignDocs || this.currentDesignReviewer)) {
        this.isDesignCommentFulfilled = false
        this.completeErrMsg = i18n.t('tenantProjects.cpNotYetReviewed')
      } else if (designComment.comment === DesignBatchStatus.DISAPPROVE) {
        this.completeErrMsg = i18n.t('tenantProjects.cpDisapproved')
      } else if (designComment.comment === DesignBatchStatus.PROCEED_CP_FOLLOW_UP) {
        this.isDesignCommentFulfilled = false
        this.hasChangedToFU = true
        this.completeErrMsg = i18n.t('tenantProjects.cpFollowUpRequired')
      }
    },
    validateReceivers () {
      fusionInputCheckUtil.validateAllFields(undefined, '#previewSubmissionModal')
      return fusionInputCheckUtil.isAllFieldsValid('.receivers-list')
    },
    async isValidSubmit () {
      if (!this.validateReceivers()) {
        return false
      }
      const submission = await getTenantSubmission(this.currentTenantSubmission.id)
      if (submission.completedDate) {
        notification.fireNotification(false, this.$i18n.t('tenantProjects.alreadyCompleted'))
        await this.loadTenantProject(this.$router.currentRoute.params.id)
        this.closeModal()
        return false
      }
      const latestDesignComment = lodash.get(submission, 'designComment', {})
      if (latestDesignComment.comment !== lodash.get(this.currentTenantSubmission, 'submittedDesignComment.comment')) {
        this.validateDesignComments(latestDesignComment)
        this.isDesignCommentFulfilled = false
        return false
      }

      return true
    },

    async openSubmitConfirm () {
      const isValid = await this.isValidSubmit()
      if (!isValid) {
        return
      }
      this.closeModal()
      this.$refs.submitConfirmModal.open(this.completeSubmission)
    },

    async completeSubmission () {
      const isValid = await this.isValidSubmit()
      if (!isValid) {
        return
      }
      await this.send()
      this.hasChangedToFU = false
    },
    async send () {
      await this.saveReceivers(false)

      this.setStartReview({value: false})
      if (!this.isDesignCommentFulfilled) {
        return
      }
      EventBus.$emit('tenant_docs_refresh')
      this.updateFieldByPath({path: ['submissions', this.activeSubmissionIndex, 'completedDate'], newValue: new Date()})
      const extMetaData = {
        to: this.splitEmails(this.to),
        cc: this.splitEmails(this.cc),
        bcc: this.splitEmails(this.bcc),
        docIds: this.files.map(file => file.id),
        action: 'SEND',
      }
      processDuplicateEmails(extMetaData.to, extMetaData.cc, extMetaData.bcc)
      this.logSubmissionAction({
        actionType: 'SEND',
        emailReceivers: this.emailReceivers,
      })
      if (this.isDraft && this.isReinstatement) {
        await this.sendAndSubmittedReinstatement(extMetaData)
      } else {
        await this.updateCurrentTenantSubmission({})
        await sendEmail(EmailTypes.tenantProjects.completeSubmission, extMetaData, false)
      }
      this.loadTenantProject(this.tpId)
      this.closeModal()
    },
  },
}
</script>

<style scoped lang="scss">
@import '~styles/settings';
@import '~styles/popup';
.reveal#previewSubmissionModal {
  top: 0px !important;
}
</style>

<style lang="scss">
.tooltip {
  z-index: 1200;
}
#previewSubmissionModal {
  $tender-font-color: #0C3A58;
  overflow: auto;
  width:75%;
  padding-left: 30px;
  padding-right: 30px;
  .submission-email-subject {
    & > label {
      color: $tender-font-color;
      font-size: 14px;
      font-weight: bold;
    }
  }
  .text-input {
    margin: 0 1px;
    textarea {
      border: solid 1px #B5B5B5;
      min-height: 20px;
      width: 95%;
      margin-bottom: 5px;
      height: 40px;
      color: $tender-font-color;
      overflow: hidden;
      padding-left: 10px;
    }

    &>label {
      text-transform: capitalize;
      margin-top: 15px;
      margin-bottom: 5px;
      font-size: 14px;
      line-height: 1.43;
      font-weight: bold;
      text-align: left;
      color: $tender-font-color;
    }
  }
  .consolidated-comment {
    .comment-content {
      padding: 10px 0;
      font-weight: normal;
    }
    .comment-content.description {
      padding: 0 0 10px 0;
    }
    .section-header {
      font-weight: bold;
      padding: 12px 0;
    }
  }
  .comments-preview-body-warpper {
    margin-top: 24px;
  }
  .error-msg {
    color: red;
    text-align: right;
    margin-top: 10px;
  }
}

</style>
